import Job from 'jobs/Job'
import { action, computed, observable, makeObservable } from 'mobx'
import { required, validationContext } from 'validx'
import { CursorPaged, CursorPageQuery } from 'types/definitions'
import Member from 'iam/models/Member'
import { FindMembers } from 'iam/MemberStore'
import { BehaviorSubject } from 'rxjs'
import { CancellationToken } from 'infra/cancellation'
import ProviderStore from 'jobs/ProviderStore'

export default class ProInputEditorViewModel {
  /**
   * Tracks whether the view model is active.
   */
  private readonly _active = new BehaviorSubject<boolean>(false)

  /**
   * Pro.
   */
  @observable
  pro?: Member | null = undefined

  @observable
  jobs: Job[] = []

  /**
   * Whether we can display all pros.
   */
  @observable
  canDisplayAllPros = false

  /**
   * Whether we can display qualified pros.
   */
  @observable
  canDisplayQualifiedPros = true

  constructor(
    private findMembers: FindMembers,
    private providerStore: ProviderStore,
  ) {
    makeObservable(this)
  }

  /**
   * Validation context for pro.
   */
  validation = validationContext(this, {
    pro: [required('Pro is required')],
  })

  /**
   * Getter for the current job champion pro.
   */
  @computed
  get currentChampionPro() {
    return this.jobs[0].championProvider?.member
  }

  /**
   * Sets dropdown to show all pros.
   */
  @action.bound
  setDisplayAllPros() {
    this.canDisplayAllPros = true
    this.canDisplayQualifiedPros = false
  }

  /**
   * Sets dropdown to show only qualifed pros.
   */
  @action.bound
  setDisplayQualifiedPros() {
    this.canDisplayAllPros = false
    this.canDisplayQualifiedPros = true
  }

  /**
   * Sets pro to transfer.
   */
  @action.bound
  setPro(pro?: Member | null) {
    this.pro = pro
  }

  /**
   * Indicates if pro is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.validation.reset()
    this.pro = undefined
    this.canDisplayAllPros = false
    this.canDisplayQualifiedPros = true
  }

  activate(jobs: Job[]) {
    this._active.next(true)
    this.jobs = jobs
  }

  deactivate() {
    this._active.next(false)
  }

  /**
   * Fetches pros.
   */
  fetchPros = async (
    searchText: string,
    params: CursorPageQuery,
    cancellationToken = CancellationToken.default,
  ): Promise<CursorPaged<Member>> => {
    const collaborators = this.jobs.map((j) => j.collaborators.items).flat()
    const paged = await this.findMembers(
      {
        after: params.after,
        limit: params.limit,
        workspace_id: this.jobs[0].currentMemberWorkspaceId,
        search_text: searchText,
      },
      cancellationToken,
    )

    return CursorPaged.map(paged, (data) =>
      data.filter((m) => {
        const invalidCandidate =
          collaborators.some((s) => s.member === m) ||
          m.user.id === 'SYSTEM_ROBOT'
        return !invalidCandidate
      }),
    )
  }

  /**
   * Fetches qualified pros.
   */
  fetchQualifiedPros = async (
    searchText: string,
    query: CursorPageQuery,
  ): Promise<CursorPaged<Member | null>> => {
    const result = await Promise.all(
      this.jobs.map((j) =>
        this.providerStore.fetchQualifiedProviders(searchText, query, j),
      ),
    )

    // because we support multiple jobs transfer when fetching qualified pros we want to display only the pros
    // that are present in all the selected jobs
    return CursorPaged.fromArray(
      result
        .map((i) => i.data)
        .reduce((previousValue, currentValue) =>
          previousValue.filter((item) =>
            currentValue.some(
              (pro) => pro?.user.publicId === item?.user.publicId,
            ),
          ),
        ),
    )
  }
}
